import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { gameDayToShortDate } from '../utils/dateUtils';
import './BoxScore.css';

interface BoxScoreProps {
  gameId: string;
  leagueId?: string;
  onClose: () => void;
}

interface GameInfo {
  id: string;
  league_id: string;
  home_team_id: string;
  away_team_id: string;
  home_score: number;
  away_score: number;
  game_day: number;
  status: string;
  winner: string;
  home_hits?: number;
  away_hits?: number;
  home_errors?: number;
  away_errors?: number;
  line_score?: { inning: number; away_runs: number; home_runs: number }[];
}

interface TeamInfo {
  id: string;
  name: string;
  city: string;
  abbreviation: string;
}

interface PlayerGameStat {
  id: string;
  player_id: string;
  team_id: string;
  is_pitcher: boolean;
  plate_appearances: number;
  at_bats: number;
  runs: number;
  hits: number;
  doubles: number;
  triples: number;
  home_runs: number;
  rbi: number;
  walks: number;
  strikeouts: number;
  stolen_bases: number;
  innings_pitched: number;
  hits_allowed: number;
  runs_allowed: number;
  earned_runs: number;
  walks_allowed: number;
  strikeouts_pitched: number;
  home_runs_allowed: number;
  pitches_thrown: number;
  win: boolean;
  loss: boolean;
  players: {
    first_name: string;
    last_name: string;
    position: string;
  } | null;
}

const BoxScore: React.FC<BoxScoreProps> = ({ gameId, leagueId, onClose }) => {
  const [game, setGame] = useState<GameInfo | null>(null);
  const [homeTeam, setHomeTeam] = useState<TeamInfo | null>(null);
  const [awayTeam, setAwayTeam] = useState<TeamInfo | null>(null);
  const [homeBatters, setHomeBatters] = useState<PlayerGameStat[]>([]);
  const [awayBatters, setAwayBatters] = useState<PlayerGameStat[]>([]);
  const [homePitchers, setHomePitchers] = useState<PlayerGameStat[]>([]);
  const [awayPitchers, setAwayPitchers] = useState<PlayerGameStat[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBoxScore();
  }, [gameId]);

  async function loadBoxScore() {
    setLoading(true);
    if (!isSupabaseConfigured() || !supabase) { setLoading(false); return; }

    // ─── Unified Game Path ────────────────────────────────────
    // All games (regular season + playoffs) are stored in league_schedule.
    // The old playoff_games table is stale — playoff games use is_playoff=true in league_schedule.
    // IMPORTANT: game_id is not unique across leagues — must filter by league_id too
    // Use game_id.eq when leagueId is available (game_id+league_id is unique),
    // otherwise try or(game_id,id) but only when gameId looks like a UUID
    let schedQuery = supabase.from('league_schedule').select('*');
    if (leagueId) {
      schedQuery = schedQuery.eq('game_id', gameId).eq('league_id', leagueId);
    } else {
      schedQuery = schedQuery.or(`game_id.eq.${gameId},id.eq.${gameId}`);
    }
    const { data: schedData } = await schedQuery.single();

    // Load full game data from staging (line_score, hits, errors)
    let stagQuery = supabase.from('game_results_staging').select('*');
    if (leagueId) {
      stagQuery = stagQuery.eq('game_id', gameId).eq('league_id', leagueId);
    } else {
      stagQuery = stagQuery.or(`game_id.eq.${gameId},id.eq.${gameId}`);
    }
    const { data: stagData } = await stagQuery.maybeSingle();

    if (!schedData) { setLoading(false); return; }

    // Merge: schedule gives scores/game_day/team_ids, staging gives hits/errors/line_score
    // line_score may be a JSON string from staging — parse it if so
    const rawLineScore = stagData?.line_score ?? schedData.line_score;
    const parsedLineScore = typeof rawLineScore === 'string' ? JSON.parse(rawLineScore) : rawLineScore;
    // Normalize: DB may store half-inning format [{half:"T1",runs:0}...] → convert to [{inning:1,away_runs:0,home_runs:0}...]
    let lineScore: Array<{inning: number; away_runs: number; home_runs: number; away_hits?: number; home_hits?: number}> = [];
    if (parsedLineScore && Array.isArray(parsedLineScore)) {
      if (parsedLineScore[0]?.inning != null) {
        // Already in inning format
        lineScore = parsedLineScore;
      } else if (parsedLineScore[0]?.half != null) {
        // Half-inning format — convert
        const map = new Map<number, any>();
        parsedLineScore.forEach((ls: any) => {
          const m = ls.half.match(/^[TB](\d+)$/);
          if (!m) return;
          const inn = parseInt(m[1]);
          if (!map.has(inn)) map.set(inn, { inning: inn, away_runs: 0, home_runs: 0, away_hits: 0, home_hits: 0 });
          const e = map.get(inn);
          if (ls.half.startsWith('T')) { e.away_runs = (e.away_runs||0)+(ls.runs||0); e.away_hits = (e.away_hits||0)+(ls.hits||0); }
          else { e.home_runs = (e.home_runs||0)+(ls.runs||0); e.home_hits = (e.home_hits||0)+(ls.hits||0); }
        });
        lineScore = Array.from(map.values()).sort((a,b) => a.inning - b.inning);
      }
    }
    const g: GameInfo = {
      ...(schedData as GameInfo),
      home_hits: stagData?.home_hits ?? schedData.home_hits,
      away_hits: stagData?.away_hits ?? schedData.away_hits,
      home_errors: stagData?.home_errors ?? schedData.home_errors,
      away_errors: stagData?.away_errors ?? schedData.away_errors,
      line_score: lineScore,
    };
    setGame(g);

    // Load teams
    const { data: teamsData } = await supabase
      .from('teams')
      .select('id, name, city, abbreviation')
      .in('id', [g.home_team_id, g.away_team_id]);

    if (teamsData) {
      const teams = teamsData as TeamInfo[];
      setHomeTeam(teams.find(t => t.id === g.home_team_id) || null);
      setAwayTeam(teams.find(t => t.id === g.away_team_id) || null);
    }

    // Load player game stats (simple query, no join)
    // Filter by league_id too — game_id is not globally unique
    let statsQuery = supabase.from('player_game_stats').select('*');
    if (leagueId) {
      statsQuery = statsQuery.eq('game_id', gameId).eq('league_id', leagueId);
    } else {
      statsQuery = statsQuery.or(`game_id.eq.${gameId},id.eq.${gameId}`);
    }
    const { data: statsData, error: statsError } = await statsQuery;

    if (statsError) {
      console.error('❌ BoxScore stats query error:', statsError);
    }

    if (statsData) {
      // Load player names separately
      const playerIds = Array.from(new Set(statsData.map((s: any) => s.player_id)));
      const { data: playersData } = await supabase
        .from('players')
        .select('id, first_name, last_name, position')
        .in('id', playerIds);

      const playerMap = new Map<string, any>();
      (playersData || []).forEach((p: any) => playerMap.set(p.id, p));

      const all = statsData.map((s: any) => ({
        ...s,
        players: playerMap.get(s.player_id) || null,
      })) as any as PlayerGameStat[];

      setHomeBatters(all.filter(s => s.team_id === g.home_team_id && !s.is_pitcher));
      setAwayBatters(all.filter(s => s.team_id === g.away_team_id && !s.is_pitcher));
      setHomePitchers(all.filter(s => s.team_id === g.home_team_id && s.is_pitcher));
      setAwayPitchers(all.filter(s => s.team_id === g.away_team_id && s.is_pitcher));

      console.log(`📊 BoxScore loaded: ${all.length} stat records for game ${gameId}`);
    }

    setLoading(false);
  }

  const calcAvg = (h: number, ab: number): string => ab > 0 ? (h / ab).toFixed(3).replace(/^0/, '') : '.000';
  const calcEra = (er: number, ip: number): string => ip > 0 ? ((er * 9) / ip).toFixed(2) : '-.--';

  if (loading) {
    return (
      <div className="boxscore-overlay" onClick={onClose}>
        <div className="boxscore-modal" onClick={e => e.stopPropagation()}>
          <div className="loading-container"><div className="spinner" /><p>Loading boxscore...</p></div>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="boxscore-overlay" onClick={onClose}>
        <div className="boxscore-modal" onClick={e => e.stopPropagation()}>
          <button className="boxscore-close" onClick={onClose}>×</button>
          <p>Game not found</p>
        </div>
      </div>
    );
  }

  const homeWon = game.home_score > game.away_score;

  return (
    <div className="boxscore-overlay" onClick={onClose}>
      <div className="boxscore-modal" onClick={e => e.stopPropagation()}>
        <button className="boxscore-close" onClick={onClose}>×</button>

        {/* Header */}
        <div className="boxscore-header">
          <h2>Box Score — {gameDayToShortDate(game.game_day)}</h2>
        </div>

        {/* Score Summary */}
        <div className="boxscore-score">
          <div className={`score-team ${!homeWon ? 'winner' : ''}`}>
            <span className="team-label" style={{cursor:'pointer',color:'#60a5fa'}} onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: `/team/${game.away_team_id}` }))}>{awayTeam ? (awayTeam.name.startsWith(awayTeam.city) ? awayTeam.name : `${awayTeam.city} ${awayTeam.name}`) : 'Away'}</span>
            <span className="team-score">{game.away_score}</span>
          </div>
          <div className="score-at">@</div>
          <div className={`score-team ${homeWon ? 'winner' : ''}`}>
            <span className="team-label" style={{cursor:'pointer',color:'#60a5fa'}} onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: `/team/${game.home_team_id}` }))}>{homeTeam ? (homeTeam.name.startsWith(homeTeam.city) ? homeTeam.name : `${homeTeam.city} ${homeTeam.name}`) : 'Home'}</span>
            <span className="team-score">{game.home_score}</span>
          </div>
        </div>

        {/* Line Score */}
        {game.line_score && game.line_score.length > 0 && (
          <div className="line-score">
            <table className="line-score-table">
              <thead>
                <tr>
                  <th></th>
                  {game.line_score.map(ls => <th key={ls.inning}>{ls.inning}</th>)}
                  <th><strong>R</strong></th>
                  <th><strong>H</strong></th>
                  <th><strong>E</strong></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="team-abbr">{awayTeam?.abbreviation || 'Away'}</td>
                  {game.line_score.map(ls => <td key={ls.inning}>{ls.away_runs ?? '-'}</td>)}
                  <td><strong>{game.away_score}</strong></td>
                  <td><strong>{game.away_hits || 0}</strong></td>
                  <td><strong>{game.away_errors || 0}</strong></td>
                </tr>
                <tr>
                  <td className="team-abbr">{homeTeam?.abbreviation || 'Home'}</td>
                  {game.line_score.map(ls => <td key={ls.inning}>{ls.home_runs ?? '-'}</td>)}
                  <td><strong>{game.home_score}</strong></td>
                  <td><strong>{game.home_hits || 0}</strong></td>
                  <td><strong>{game.home_errors || 0}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Away Team Batting */}
        <div className="boxscore-section">
          <h3>{awayTeam?.abbreviation || 'Away'} Batting</h3>
          {awayBatters.length === 0 ? (
            <p className="no-stats">No batting stats available for this game.</p>
          ) : (
          <table className="boxscore-table">
            <thead>
              <tr>
                <th>Player</th>
                <th>AB</th>
                <th>R</th>
                <th>H</th>
                <th>2B</th>
                <th>3B</th>
                <th>HR</th>
                <th>RBI</th>
                <th>BB</th>
                <th>SO</th>
                <th>AVG</th>
              </tr>
            </thead>
            <tbody>
              {awayBatters.map(p => (
                <tr key={p.id}>
                  <td className="player-name"><span className="player-link" onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: `/player/${p.player_id}` }))}>{p.players?.first_name} {p.players?.last_name}</span> <span className="pos">{p.players?.position}</span></td>
                  <td>{p.at_bats}</td>
                  <td>{p.runs}</td>
                  <td>{p.hits}</td>
                  <td>{p.doubles}</td>
                  <td>{p.triples}</td>
                  <td>{p.home_runs}</td>
                  <td>{p.rbi}</td>
                  <td>{p.walks}</td>
                  <td>{p.strikeouts}</td>
                  <td>{calcAvg(p.hits, p.at_bats)}</td>
                </tr>
              ))}
              {/* Totals row */}
              <tr className="totals-row">
                <td><strong>Total</strong></td>
                <td><strong>{awayBatters.reduce((s, p) => s + p.at_bats, 0)}</strong></td>
                <td><strong>{awayBatters.reduce((s, p) => s + p.runs, 0)}</strong></td>
                <td><strong>{awayBatters.reduce((s, p) => s + p.hits, 0)}</strong></td>
                <td><strong>{awayBatters.reduce((s, p) => s + p.doubles, 0)}</strong></td>
                <td><strong>{awayBatters.reduce((s, p) => s + p.triples, 0)}</strong></td>
                <td><strong>{awayBatters.reduce((s, p) => s + p.home_runs, 0)}</strong></td>
                <td><strong>{awayBatters.reduce((s, p) => s + p.rbi, 0)}</strong></td>
                <td><strong>{awayBatters.reduce((s, p) => s + p.walks, 0)}</strong></td>
                <td><strong>{awayBatters.reduce((s, p) => s + p.strikeouts, 0)}</strong></td>
                <td><strong>{calcAvg(awayBatters.reduce((s, p) => s + p.hits, 0), awayBatters.reduce((s, p) => s + p.at_bats, 0))}</strong></td>
              </tr>
            </tbody>
          </table>
          )}
        </div>

        {/* Home Team Batting */}
        <div className="boxscore-section">
          <h3>{homeTeam?.abbreviation || 'Home'} Batting</h3>
          {homeBatters.length === 0 ? (
            <p className="no-stats">No batting stats available for this game.</p>
          ) : (
          <table className="boxscore-table">
            <thead>
              <tr>
                <th>Player</th>
                <th>AB</th>
                <th>R</th>
                <th>H</th>
                <th>2B</th>
                <th>3B</th>
                <th>HR</th>
                <th>RBI</th>
                <th>BB</th>
                <th>SO</th>
                <th>AVG</th>
              </tr>
            </thead>
            <tbody>
              {homeBatters.map(p => (
                <tr key={p.id}>
                  <td className="player-name"><span className="player-link" onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: `/player/${p.player_id}` }))}>{p.players?.first_name} {p.players?.last_name}</span> <span className="pos">{p.players?.position}</span></td>
                  <td>{p.at_bats}</td>
                  <td>{p.runs}</td>
                  <td>{p.hits}</td>
                  <td>{p.doubles}</td>
                  <td>{p.triples}</td>
                  <td>{p.home_runs}</td>
                  <td>{p.rbi}</td>
                  <td>{p.walks}</td>
                  <td>{p.strikeouts}</td>
                  <td>{calcAvg(p.hits, p.at_bats)}</td>
                </tr>
              ))}
              <tr className="totals-row">
                <td><strong>Total</strong></td>
                <td><strong>{homeBatters.reduce((s, p) => s + p.at_bats, 0)}</strong></td>
                <td><strong>{homeBatters.reduce((s, p) => s + p.runs, 0)}</strong></td>
                <td><strong>{homeBatters.reduce((s, p) => s + p.hits, 0)}</strong></td>
                <td><strong>{homeBatters.reduce((s, p) => s + p.doubles, 0)}</strong></td>
                <td><strong>{homeBatters.reduce((s, p) => s + p.triples, 0)}</strong></td>
                <td><strong>{homeBatters.reduce((s, p) => s + p.home_runs, 0)}</strong></td>
                <td><strong>{homeBatters.reduce((s, p) => s + p.rbi, 0)}</strong></td>
                <td><strong>{homeBatters.reduce((s, p) => s + p.walks, 0)}</strong></td>
                <td><strong>{homeBatters.reduce((s, p) => s + p.strikeouts, 0)}</strong></td>
                <td><strong>{calcAvg(homeBatters.reduce((s, p) => s + p.hits, 0), homeBatters.reduce((s, p) => s + p.at_bats, 0))}</strong></td>
              </tr>
            </tbody>
          </table>
          )}
        </div>

        {/* Away Pitching */}
        <div className="boxscore-section">
          <h3>{awayTeam?.abbreviation || 'Away'} Pitching</h3>
          {awayPitchers.length === 0 ? (
            <p className="no-stats">No pitching stats available for this game.</p>
          ) : (
          <table className="boxscore-table">
            <thead>
              <tr>
                <th>Player</th>
                <th>IP</th>
                <th>H</th>
                <th>R</th>
                <th>ER</th>
                <th>BB</th>
                <th>SO</th>
                <th>Pit</th>
                <th>HR</th>
                <th>ERA</th>
              </tr>
            </thead>
            <tbody>
              {awayPitchers.map(p => (
                <tr key={p.id}>
                  <td className="player-name">
                    <span className="player-link" onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: `/player/${p.player_id}` }))}>{p.players?.first_name} {p.players?.last_name}</span>
                    {p.win && <span className="tag win">W</span>}
                    {p.loss && <span className="tag loss">L</span>}
                  </td>
                  <td>{p.innings_pitched}</td>
                  <td>{p.hits_allowed}</td>
                  <td>{p.runs_allowed}</td>
                  <td>{p.earned_runs}</td>
                  <td>{p.walks_allowed}</td>
                  <td>{p.strikeouts_pitched}</td>
                  <td>{p.pitches_thrown}</td>
                  <td>{p.home_runs_allowed}</td>
                  <td>{calcEra(p.earned_runs, p.innings_pitched)}</td>
                </tr>
              ))}
              <tr className="totals-row">
                <td><strong>Total</strong></td>
                <td><strong>{awayPitchers.reduce((s, p) => s + p.innings_pitched, 0).toFixed(1)}</strong></td>
                <td><strong>{awayPitchers.reduce((s, p) => s + p.hits_allowed, 0)}</strong></td>
                <td><strong>{awayPitchers.reduce((s, p) => s + p.runs_allowed, 0)}</strong></td>
                <td><strong>{awayPitchers.reduce((s, p) => s + p.earned_runs, 0)}</strong></td>
                <td><strong>{awayPitchers.reduce((s, p) => s + p.walks_allowed, 0)}</strong></td>
                <td><strong>{awayPitchers.reduce((s, p) => s + p.strikeouts_pitched, 0)}</strong></td>
                <td><strong>{awayPitchers.reduce((s, p) => s + p.pitches_thrown, 0)}</strong></td>
                <td><strong>{awayPitchers.reduce((s, p) => s + p.home_runs_allowed, 0)}</strong></td>
                <td><strong>{calcEra(awayPitchers.reduce((s, p) => s + p.earned_runs, 0), awayPitchers.reduce((s, p) => s + p.innings_pitched, 0))}</strong></td>
              </tr>
            </tbody>
          </table>
          )}
        </div>

        {/* Home Pitching */}
        <div className="boxscore-section">
          <h3>{homeTeam?.abbreviation || 'Home'} Pitching</h3>
          {homePitchers.length === 0 ? (
            <p className="no-stats">No pitching stats available for this game.</p>
          ) : (
          <table className="boxscore-table">
            <thead>
              <tr>
                <th>Player</th>
                <th>IP</th>
                <th>H</th>
                <th>R</th>
                <th>ER</th>
                <th>BB</th>
                <th>SO</th>
                <th>Pit</th>
                <th>HR</th>
                <th>ERA</th>
              </tr>
            </thead>
            <tbody>
              {homePitchers.map(p => (
                <tr key={p.id}>
                  <td className="player-name">
                    <span className="player-link" onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: `/player/${p.player_id}` }))}>{p.players?.first_name} {p.players?.last_name}</span>
                    {p.win && <span className="tag win">W</span>}
                    {p.loss && <span className="tag loss">L</span>}
                  </td>
                  <td>{p.innings_pitched}</td>
                  <td>{p.hits_allowed}</td>
                  <td>{p.runs_allowed}</td>
                  <td>{p.earned_runs}</td>
                  <td>{p.walks_allowed}</td>
                  <td>{p.strikeouts_pitched}</td>
                  <td>{p.pitches_thrown}</td>
                  <td>{p.home_runs_allowed}</td>
                  <td>{calcEra(p.earned_runs, p.innings_pitched)}</td>
                </tr>
              ))}
              <tr className="totals-row">
                <td><strong>Total</strong></td>
                <td><strong>{homePitchers.reduce((s, p) => s + p.innings_pitched, 0).toFixed(1)}</strong></td>
                <td><strong>{homePitchers.reduce((s, p) => s + p.hits_allowed, 0)}</strong></td>
                <td><strong>{homePitchers.reduce((s, p) => s + p.runs_allowed, 0)}</strong></td>
                <td><strong>{homePitchers.reduce((s, p) => s + p.earned_runs, 0)}</strong></td>
                <td><strong>{homePitchers.reduce((s, p) => s + p.walks_allowed, 0)}</strong></td>
                <td><strong>{homePitchers.reduce((s, p) => s + p.strikeouts_pitched, 0)}</strong></td>
                <td><strong>{homePitchers.reduce((s, p) => s + p.pitches_thrown, 0)}</strong></td>
                <td><strong>{homePitchers.reduce((s, p) => s + p.home_runs_allowed, 0)}</strong></td>
                <td><strong>{calcEra(homePitchers.reduce((s, p) => s + p.earned_runs, 0), homePitchers.reduce((s, p) => s + p.innings_pitched, 0))}</strong></td>
              </tr>
            </tbody>
          </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default BoxScore;
